/**
 * Raw-REPL-Protokoll von MicroPython ueber Web Serial.
 *
 * Der micro:bit wird ueber WebUSB und DAP.js mit einer HEX-Datei beschrieben.
 * Fuer den Pico 2 W passt das nicht: das Board meldet sich als serielles
 * Geraet und nimmt Python-Dateien ueber die REPL entgegen (W05). Diese Datei
 * spricht dieses Protokoll.
 *
 * Zwei Betriebsarten, bewusst unterschiedlich eingesetzt:
 *
 * Raw-REPL (Ctrl-A)
 *     Kein Echo, keine Eingabeaufforderung, klar abgegrenzte Antworten.
 *     Verwendet fuer Dateiuebertragung und kurze Abfragen, wo ein eindeutiges
 *     Ergebnis gebraucht wird.
 *
 * Normale REPL (Ctrl-B)
 *     Ausgaben kommen laufend an, und Ctrl-C bricht das laufende Programm ab.
 *     Verwendet zum Starten des Schuelerprogramms, weil Konsole und Stopp
 *     genau dieses Verhalten brauchen (W08).
 *
 * Waehrend einer Dateiuebertragung hat diese Schicht ausschliesslichen Zugriff
 * auf die Verbindung; Telemetrie pausiert dabei (W11).
 */

import { fehlerDaten, protokoll, vorschau } from "./protokoll";

/** Steuerzeichen der MicroPython-REPL. */
export const CTRL_A = "\x01"; // Raw-REPL betreten
export const CTRL_B = "\x02"; // Normale REPL betreten
export const CTRL_C = "\x03"; // Laufendes Programm abbrechen
export const CTRL_D = "\x04"; // Ausfuehren bzw. Soft-Reset
export const CTRL_E = "\x05"; // Raw paste mode anfordern

/**
 * Was das Geraet auf ``\x05A\x01`` antwortet.
 *
 * ``R\x01`` kann es, ``R\x00`` kennt den Befehl und kann es nicht. Alles
 * andere heisst: es kennt den Befehl gar nicht, hat die drei Zeichen als
 * Eingabe genommen und ist in die Raw-REPL zurueckgefallen.
 */
const PASTE_JA = 0x01;
const PASTE_NEIN = 0x00;
const PASTE_R = 0x52; // "R"

/** Groesse eines Uebertragungsstuecks in Bytes vor der Base64-Kodierung. */
const CHUNK_BYTES = 512;

export class PicoReplError extends Error {
  constructor(
    message: string,
    readonly detail?: string
  ) {
    super(message);
    this.name = "PicoReplError";
  }
}

export interface ExecResult {
  /** Alles, was das Programm auf stdout geschrieben hat. */
  output: string;
  /** Der Traceback, falls die Ausfuehrung mit einer Ausnahme endete. */
  error: string;
}

/**
 * Liest von einem seriellen Anschluss und verteilt die Zeichen zwischen
 * Protokollschritten und Konsole.
 *
 * Es gibt nur einen Leser je Anschluss. Ein Protokollschritt meldet mit
 * {@link startCapture} an, dass die folgenden Zeichen ihm gehoeren; alles
 * ausserhalb geht an die Konsole.
 *
 * Warum die Aufnahme **vor** dem Senden beginnen muss: die Leseschleife laeuft
 * unabhaengig vom sendenden Code. Ein schnell antwortendes Board kann seine
 * Antwort liefern, bevor der Sender ueberhaupt dazu kommt, auf sie zu warten.
 * Wuerde erst beim Warten gepuffert, ginge diese Antwort verloren und der
 * Schritt liefe in die Zeitueberschreitung.
 */
/** Worauf ein Protokollschritt gerade wartet. */
type Warten =
  | {
      art: "folge";
      nadel: Uint8Array;
      resolve: (wert: string) => void;
      reject: (fehler: Error) => void;
    }
  | {
      art: "anzahl";
      anzahl: number;
      resolve: (wert: Uint8Array) => void;
      reject: (fehler: Error) => void;
    };

/** ASCII-Bytes einer Protokollzeichenfolge. */
const alsBytes = (text: string) => new TextEncoder().encode(text);

export class SerialReader {
  /**
   * Eingegangene **Bytes**, nicht Zeichen.
   *
   * Der raw paste mode nennt seine Fenstergroesse als zwei rohe Bytes; bei 128
   * ist das ``0x80``. Durch einen UTF-8-Dekodierer wird daraus ein
   * Ersatzzeichen, und die Zahl ist weg. Deshalb liegt hier der rohe Strom,
   * und dekodiert wird erst, wenn feststeht, dass es Konsolentext ist.
   */
  private buffer = new Uint8Array(0);
  private capture = 0;
  private waiter: Warten | undefined;
  private closed = false;
  /**
   * Fortlaufender Dekodierer fuer die Konsole.
   *
   * Fortlaufend, weil ein Umlaut auf zwei Sendungen fallen kann. Ein frischer
   * Dekodierer je Sendung machte daraus zwei Ersatzzeichen.
   */
  private readonly decoder = new TextDecoder();

  constructor(private readonly onConsoleData: (text: string) => void) {}

  /** Nimmt neu eingetroffene Bytes entgegen. */
  push(data: Uint8Array): void {
    if (this.capture > 0) {
      const zusammen = new Uint8Array(this.buffer.length + data.length);
      zusammen.set(this.buffer);
      zusammen.set(data, this.buffer.length);
      this.buffer = zusammen;
      this.tryResolve();
      return;
    }
    this.onConsoleData(this.decoder.decode(data, { stream: true }));
  }

  /** Wie viele Bytes ungelesen bereitliegen. */
  get bereit(): number {
    return this.buffer.length;
  }

  /**
   * Beginnt einen Protokollschritt. Aufrufe duerfen verschachtelt sein.
   */
  startCapture(): void {
    this.capture += 1;
  }

  /**
   * Beendet einen Protokollschritt.
   *
   * Was am Ende noch im Puffer liegt, gehoerte zum Protokoll und wird
   * verworfen - es in die Konsole zu schreiben wuerde Steuerzeichen und
   * Eingabeaufforderungen in die Schuelerausgabe mischen.
   */
  stopCapture(): void {
    if (this.capture > 0) {
      this.capture -= 1;
    }
    if (this.capture === 0) {
      this.buffer = new Uint8Array(0);
    }
  }

  /** Sucht eine Bytefolge im Puffer. -1, wenn sie nicht vollstaendig da ist. */
  private suche(nadel: Uint8Array): number {
    const heu = this.buffer;
    for (let i = 0; i + nadel.length <= heu.length; i++) {
      let passt = true;
      for (let k = 0; k < nadel.length; k++) {
        if (heu[i + k] !== nadel[k]) {
          passt = false;
          break;
        }
      }
      if (passt) {
        return i;
      }
    }
    return -1;
  }

  private nimm(anzahl: number): Uint8Array {
    const stueck = this.buffer.slice(0, anzahl);
    this.buffer = this.buffer.slice(anzahl);
    return stueck;
  }

  private tryResolve(): void {
    const waiter = this.waiter;
    if (!waiter) {
      return;
    }
    if (waiter.art === "folge") {
      const index = this.suche(waiter.nadel);
      if (index < 0) {
        return;
      }
      const davor = this.nimm(index);
      this.nimm(waiter.nadel.length);
      this.waiter = undefined;
      // Einmalig dekodiert: was hier steht, ist ein abgeschlossenes Stueck.
      waiter.resolve(new TextDecoder().decode(davor));
      return;
    }
    if (this.buffer.length < waiter.anzahl) {
      return;
    }
    const stueck = this.nimm(waiter.anzahl);
    this.waiter = undefined;
    waiter.resolve(stueck);
  }

  /**
   * Wartet, bis die angegebene Zeichenfolge im Puffer steht, und liefert alles
   * davor. Bereits eingetroffene Zeichen zaehlen mit.
   *
   * @throws {PicoReplError} bei Zeitueberschreitung oder geschlossener Verbindung
   */
  async until(needle: string, timeoutMs = 5000): Promise<string> {
    const nadel = alsBytes(needle);
    return new Promise<string>((resolve, reject) => {
      if (this.belegt(reject)) {
        return;
      }
      const timer = this.zeitwaechter(timeoutMs, reject);
      this.waiter = {
        art: "folge",
        nadel,
        resolve: (wert) => {
          clearTimeout(timer);
          resolve(wert);
        },
        reject: (fehler) => {
          clearTimeout(timer);
          reject(fehler);
        },
      };
      // Die Antwort kann schon vollstaendig im Puffer stehen.
      this.tryResolve();
    });
  }

  /**
   * Wartet auf **genau** so viele Bytes und liefert sie roh.
   *
   * Fuer den Handschlag des raw paste mode: dort steht die Fenstergroesse in
   * zwei Bytes, die kein Zeichen ergeben und nach denen sich nicht suchen
   * laesst. Es gibt nur eines: abzaehlen.
   */
  async take(count: number, timeoutMs = 5000): Promise<Uint8Array> {
    return new Promise<Uint8Array>((resolve, reject) => {
      if (this.belegt(reject)) {
        return;
      }
      const timer = this.zeitwaechter(timeoutMs, reject);
      this.waiter = {
        art: "anzahl",
        anzahl: count,
        resolve: (wert) => {
          clearTimeout(timer);
          resolve(wert);
        },
        reject: (fehler) => {
          clearTimeout(timer);
          reject(fehler);
        },
      };
      this.tryResolve();
    });
  }

  /** Meldet ueber ``reject``, wenn gerade nicht gewartet werden kann. */
  private belegt(reject: (fehler: Error) => void): boolean {
    if (this.closed) {
      reject(new PicoReplError("Die Verbindung zum Board ist geschlossen."));
      return true;
    }
    if (this.waiter) {
      reject(new PicoReplError("Es wird bereits auf eine Antwort gewartet."));
      return true;
    }
    return false;
  }

  private zeitwaechter(timeoutMs: number, reject: (fehler: Error) => void) {
    return setTimeout(() => {
      const wartete = this.waiter;
      protokoll.ereignis("repl", "zeitueberschreitung", {
        frist_ms: timeoutMs,
        wartete_auf:
          wartete?.art === "folge"
            ? vorschau(wartete.nadel)
            : wartete?.art === "anzahl"
              ? `${wartete.anzahl} Byte`
              : undefined,
        puffer_bytes: this.buffer.length,
        puffer_ende: vorschau(this.buffer.slice(-120)),
      });
      this.waiter = undefined;
      reject(
        new PicoReplError(
          `Das Board hat innerhalb von ${timeoutMs} ms nicht geantwortet.`,
          new TextDecoder().decode(this.buffer.slice(-200))
        )
      );
    }, timeoutMs);
  }

  /** Verwirft alles, was noch ungelesen im Puffer liegt. */
  discard(): void {
    this.buffer = new Uint8Array(0);
  }

  close(): void {
    this.closed = true;
    this.waiter?.reject(new PicoReplError("Die Verbindung wurde getrennt."));
    this.waiter = undefined;
  }
}

/**
 * Spricht das REPL-Protokoll auf einem bereits geoeffneten Anschluss.
 */
export class RawRepl {
  private inRawMode = false;
  /**
   * Ob der raw paste mode noch versucht wird.
   *
   * Sagt ein Geraet einmal nein, sagt es immer nein - das haengt an seiner
   * Firmware. Ein zweiter Versuch kostete nur einen Umlauf je Uebertragung.
   */
  private pasteMoeglich = true;

  constructor(
    private readonly reader: SerialReader,
    private readonly write: (text: string) => Promise<void>
  ) {}

  /** Bricht ein laufendes Programm ab (Ctrl-C). */
  async interrupt(): Promise<void> {
    await this.write(CTRL_C);
  }

  /**
   * Fuehrt einen Protokollschritt aus.
   *
   * Die Aufnahme beginnt vor dem ersten Senden, damit eine schnelle Antwort
   * nicht verloren geht.
   */
  private async step<T>(body: () => Promise<T>): Promise<T> {
    this.reader.startCapture();
    try {
      return await body();
    } finally {
      this.reader.stopCapture();
    }
  }

  /**
   * Betritt die Raw-REPL.
   *
   * Zweimal Ctrl-C, weil ein Programm in einer engen Schleife das erste
   * Signal verschlucken kann.
   */
  async enterRaw(): Promise<void> {
    if (this.inRawMode) {
      return;
    }
    const beginn = performance.now();
    protokoll.ereignis("repl", "raw-repl betreten");
    await this.step(async () => {
      await this.write(CTRL_C);
      await this.write(CTRL_C);
      this.reader.discard();
      await this.write(CTRL_A);
      try {
        await this.reader.until("raw REPL; CTRL-B to exit\r\n>");
        protokoll.ereignis("repl", "raw-repl bereit", {
          nach_ms: Math.round(performance.now() - beginn),
        });
      } catch (error) {
        protokoll.ereignis("repl", "raw-repl nicht erreicht", fehlerDaten(error));
        // Der haeufigste Grund ist kein Uebertragungsfehler, sondern ein
        // Programm, das den Abbruch abfaengt:
        //
        //     while True:
        //         try:
        //             ...
        //         except Exception:     # faengt auch KeyboardInterrupt nicht,
        //             ...               # aber ein eigenes except dafuer schon
        //
        // Der Stopp-Knopf haengt an genau diesem Signal. Faengt das Programm
        // es ab, kommt niemand mehr an den Roboter heran - ueber Kabel so
        // wenig wie ueber Funk. Am 11.09.2026 ist uns das selbst passiert, und
        // die Meldung sagte nur "Fehler, wenden Sie sich an den Support".
        throw new PicoReplError(
          "Der Roboter antwortet nicht auf den Stopp. Meist liegt das an " +
            "einem Programm, das den Abbruch selbst abfaengt - dann hilft nur, " +
            "den Roboter kurz abzuziehen und wieder einzustecken.",
          error instanceof PicoReplError ? error.detail : undefined
        );
      }
    });
    this.inRawMode = true;
  }

  /** ``true``, solange die Raw-REPL aktiv ist. */
  get raw(): boolean {
    return this.inRawMode;
  }

  /** Verlaesst die Raw-REPL und kehrt zur normalen REPL zurueck. */
  async exitRaw(): Promise<void> {
    if (!this.inRawMode) {
      return;
    }
    this.inRawMode = false;
    protokoll.ereignis("repl", "raw-repl verlassen");
    await this.step(async () => {
      await this.write(CTRL_B);
      await this.reader.until(">>> ", 3000).catch(() => undefined);
    });
  }

  /**
   * Fuehrt Python-Code in der Raw-REPL aus und liefert Ausgabe und Traceback.
   *
   * @throws {PicoReplError} wenn das Board den Code nicht annimmt
   */
  async exec(code: string, timeoutMs = 10000): Promise<ExecResult> {
    await this.enterRaw();
    const beginn = performance.now();
    return this.step(async () => {
      const perPaste = await this.rawPaste(code, timeoutMs);
      protokoll.ereignis("repl", "code gesendet", {
        weg: perPaste ? "raw paste" : "raw-repl",
        bytes: new TextEncoder().encode(code).length,
        anfang: vorschau(code.split("\n")[0] ?? ""),
        nach_ms: Math.round(performance.now() - beginn),
      });
      if (!perPaste) {
        await this.write(code);
        await this.write(CTRL_D);

        const acknowledgement = await this.reader.until("OK", 5000);
        if (acknowledgement.includes("could not exec")) {
          throw new PicoReplError("Das Board hat den Code nicht angenommen.", acknowledgement);
        }
      }

      const output = await this.reader.until(CTRL_D, timeoutMs);
      const error = await this.reader.until(CTRL_D, timeoutMs);
      await this.reader.until(">", 5000);
      protokoll.ereignis("repl", "code ausgefuehrt", {
        dauer_ms: Math.round(performance.now() - beginn),
        ausgabe_bytes: output.length,
        fehler: error.trim() ? vorschau(error.trim().split("\n").slice(-1)[0]) : undefined,
      });
      return { output, error };
    });
  }

  /**
   * Uebertraegt den Code mit der Flusskontrolle von MicroPython.
   *
   * Warum das sein muss (docs/messungen/2026-09-13-m1-funkverluste.md): ueber
   * Funk schreibt der Editor mit ``writeValueWithoutResponse``, und das heisst
   * woertlich, dass niemand quittiert. macOS nimmt 63 Schreibvorgaenge an und
   * wirft den Rest weg - von einem 3-kB-Programm kamen **1260 Byte** an, in
   * drei von drei Laeufen. Ohne Fehler, ohne Meldung.
   *
   * Der raw paste mode dreht die Richtung um: das Geraet nennt eine
   * Fenstergroesse und meldet mit ``\x01``, was es verbraucht hat. Der Host
   * schickt nur, was angefordert wurde. Damit sind nie mehr als zwei Fenster
   * unterwegs - bei der heutigen Firmware 256 Byte. Der Verlust wird nicht
   * kleiner, er wird **unmoeglich**.
   *
   * Der Ablauf steht in ``upstream/micropython/shared/runtime/pyexec.c``,
   * ``mp_reader_new_stdin`` und ``do_reader_stdin``.
   *
   * @returns ``false``, wenn das Geraet den raw paste mode nicht kann. Dann
   *   ist nichts geschrieben worden und der Aufrufer nimmt den alten Weg.
   */
  private async rawPaste(code: string, timeoutMs: number): Promise<boolean> {
    if (!this.pasteMoeglich) {
      return false;
    }
    await this.write(CTRL_E + "A" + "\x01");
    const antwort = await this.reader.take(2, 5000);
    protokoll.ereignis("repl", "raw paste antwort", {
      bytes: Array.from(antwort).map((b) => b.toString(16).padStart(2, "0")).join(" "),
    });
    if (antwort[0] !== PASTE_R || antwort[1] !== PASTE_JA) {
      // Einmal gelernt, nie wieder versucht: das haengt am Geraet und nicht
      // am einzelnen Aufruf.
      this.pasteMoeglich = false;
      if (antwort[0] === PASTE_R && antwort[1] === PASTE_NEIN) {
        // Befehl verstanden, kann ihn nicht. Die Raw-REPL ist unveraendert.
        return false;
      }
      // Den Befehl gar nicht gekannt: die drei Zeichen galten als Eingabe,
      // und das Geraet meldet sich mit der Ankuendigung der Raw-REPL zurueck.
      await this.reader.until("w REPL; CTRL-B to exit\r\n>", 5000);
      return false;
    }

    const kopf = await this.reader.take(2, 5000);
    // Little endian, und genau deshalb liest diese Schicht Bytes: bei einer
    // Fenstergroesse von 128 ist das untere Byte 0x80, und durch einen
    // UTF-8-Dekodierer wird daraus ein Ersatzzeichen.
    const fenster = kopf[0] | (kopf[1] << 8);
    if (fenster <= 0) {
      throw new PicoReplError(
        "Das Board hat eine unbrauchbare Fenstergroesse genannt.",
        String(fenster)
      );
    }

    const bytes = new TextEncoder().encode(code);
    let guthaben = fenster;
    let ab = 0;
    let quittungen = 0;
    while (ab < bytes.length) {
      const bis = grenzeOhneZeichenBruch(bytes, ab, guthaben);
      // Warten, wenn kein ganzes Zeichen mehr ins Guthaben passt - nicht erst
      // bei null. Die erste Fassung wartete nur bei null. Stand das Guthaben
      // auf einem Byte und kam ein Umlaut, schrieb sie einen leeren Block und
      // drehte sich, und weil dabei nur Mikrotasks liefen, kam die Funkmeldung
      // mit der naechsten Quittung nie an die Reihe. Am Roboter gefunden: er
      // stand im raw paste mode und wartete auf Daten, der Editor auf ihn.
      //
      // Das Warten kann nicht ewig dauern. Hat der Editor alle Quittungen
      // gelesen, ist das Guthaben mindestens ein Fenster gross, und ein Zeichen
      // hat hoechstens vier Bytes. Passt keines, ist also eine Quittung
      // unterwegs.
      //
      // Was schon hereingekommen ist, wird ebenfalls gelesen, bevor
      // geschrieben wird - sonst stauen sich die Quittungen, und der naechste
      // Schritt haelt sie fuer seine Antwort.
      if (bis === ab || this.reader.bereit > 0) {
        const zeichen = await this.reader.take(1, timeoutMs);
        if (zeichen[0] === 0x01) {
          guthaben += fenster;
          quittungen += 1;
        } else if (zeichen[0] === 0x04) {
          // Das Geraet bricht ab - etwa weil der Code schon beim Uebersetzen
          // scheitert. Quittieren und den Rest dem Aufrufer ueberlassen.
          protokoll.ereignis("repl", "raw paste vom geraet beendet", {
            gesendet: ab,
            von: bytes.length,
          });
          await this.write(CTRL_D);
          return true;
        } else {
          protokoll.ereignis("repl", "raw paste unerwartetes zeichen", {
            byte: zeichen[0],
            gesendet: ab,
            von: bytes.length,
          });
          throw new PicoReplError(
            "Das Board hat waehrend der Uebertragung etwas Unerwartetes geschickt.",
            String(zeichen[0])
          );
        }
        continue;
      }
      await this.write(new TextDecoder().decode(bytes.slice(ab, bis)));
      guthaben -= bis - ab;
      ab = bis;
    }

    await this.write(CTRL_D);
    // Die Quittung des Geraets, dass es das Ende gesehen hat. Danach folgt
    // die Ausgabe wie beim gewoehnlichen Weg.
    await this.reader.until(CTRL_D, 5000);
    protokoll.ereignis("repl", "raw paste fertig", {
      fenster,
      quittungen,
      bytes: bytes.length,
    });
    return true;
  }

  /**
   * Fuehrt Code aus und wirft, wenn er mit einer Ausnahme endete.
   *
   * Fuer Protokollschritte gedacht, bei denen ein Fehler ein Fehler ist -
   * nicht fuer Schuelercode, dessen Traceback in der Konsole erscheinen soll.
   */
  async execChecked(code: string, timeoutMs = 10000): Promise<string> {
    const { output, error } = await this.exec(code, timeoutMs);
    if (error.trim()) {
      throw new PicoReplError("Auf dem Board ist ein Fehler aufgetreten.", error.trim());
    }
    return output;
  }

  /**
   * Legt eine Datei auf dem Board an.
   *
   * Uebertragen wird stueckweise Base64-kodiert: die Raw-REPL ist ein
   * Textkanal, und Umlaute oder Steuerzeichen im Schuelercode wuerden roh
   * uebertragen das Protokoll stoeren.
   */
  async writeFile(path: string, contents: string | Uint8Array): Promise<void> {
    const bytes =
      typeof contents === "string" ? new TextEncoder().encode(contents) : contents;
    await this.execChecked(
      `import ubinascii\n_f=open(${JSON.stringify(path)},'wb')\n`
    );
    try {
      for (let offset = 0; offset < bytes.length; offset += CHUNK_BYTES) {
        const chunk = bytes.subarray(offset, offset + CHUNK_BYTES);
        await this.execChecked(
          `_f.write(ubinascii.a2b_base64(${JSON.stringify(base64Encode(chunk))}))\n`
        );
      }
    } finally {
      await this.execChecked("_f.close()\ndel _f\n");
    }
  }

  /** Legt ein Verzeichnis an; ein bereits vorhandenes ist kein Fehler. */
  async makeDirectory(path: string): Promise<void> {
    await this.execChecked(
      `import os\ntry:\n    os.mkdir(${JSON.stringify(path)})\nexcept OSError:\n    pass\n`
    );
  }

  /** Loescht ein Verzeichnis mit seinem gesamten Inhalt. */
  async removeTree(path: string): Promise<void> {
    await this.execChecked(
      [
        "import os",
        "def _rm(p):",
        "    try:",
        "        for e in os.listdir(p):",
        "            _rm(p + '/' + e)",
        "        os.rmdir(p)",
        "    except OSError:",
        "        try:",
        "            os.remove(p)",
        "        except OSError:",
        "            pass",
        `_rm(${JSON.stringify(path)})`,
        "del _rm",
        "",
      ].join("\n")
    );
  }
}

/** Base64 ohne Umweg ueber einen String mit Zeichen ausserhalb von Latin-1. */
function base64Encode(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Groesste Grenze ab ``ab``, die hoechstens ``hoechstens`` Bytes umfasst und
 * **kein Mehrbytezeichen zerschneidet**.
 *
 * Das Guthaben zaehlt Bytes, geschrieben wird aber Text. Ein Umlaut in einem
 * Kommentar - und die gibt es hier reichlich - belegt zwei Bytes. Mitten
 * hindurch zu schneiden ergaebe beim Zurueckwandeln ein Ersatzzeichen, und der
 * Roboter bekaeme ein anderes Programm, als der Editor geschickt hat.
 */
export function grenzeOhneZeichenBruch(
  bytes: Uint8Array,
  ab: number,
  hoechstens: number
): number {
  let bis = Math.min(ab + hoechstens, bytes.length);
  // Fortsetzungsbytes haben die Bitfolge 10xxxxxx.
  while (bis > ab && bis < bytes.length && (bytes[bis] & 0xc0) === 0x80) {
    bis -= 1;
  }
  return bis;
}
